import {
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  InputBase,
  Modal,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, typography } from "@mui/system";
import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import TableItem from "../../../components/TableItem";
import {
  getImportBillData,
  getImportBillById,
  searchImportItem,
} from "../../../services/Import";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import SubPersonSelectingPopup from "../../../components/SubPersonSelectingPopup";
import ItemSelectingPopup from "../../../components/ItemSelectingPopup";
import ConfirmationPopup from "../../../components/ConfirmationPopup";
import { RoleBasedRender } from "../../../components/RoleBaseRender";
import SubOrderProductCreate from "./SubOrderProductCreate";
import SubOrderProductEdit from "./SubOrderProductEdit";
import {
  getSubOrderProductsByPaging,
  deleteSubOrderProductById,
} from "../../../services/SubOrderProduct";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  minWidth: "100%",
  justifyContent: "flex-start",
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.2),
    borderColor: theme.palette.primary.main,
  },
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

let _subOrderProductListState = null;

function SubOrderProduct() {
  const history = useHistory();

  const [rows, setRows] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [openForm, setOpenForm] = React.useState(false);
  const [openEditForm, setOpenEditForm] = React.useState(false);
  const [editProductId, setEditProductId] = React.useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [orderLoading, setOrderLoading] = React.useState(false);
  const [itemLoading, set_itemLoading] = React.useState(false);
  const [totalPages, setTotalPages] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [openInputItemTable, setOpenInputItemTable] = useState(false);
  const [selectedInputItems, setSelectedInputItems] = useState([]);
  const [openOutputItemTable, setOpenOutputItemTable] = useState(false);
  const [selectedOutputItems, setSelectedOutputItems] = useState([]);
  const [itemRows, setItemRows] = useState([]);
  const [openSubOrderPersonTable, setOpenSubOrderPersonTable] = useState(false);
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [personRows, setPersonRows] = useState([]);


  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        if (_subOrderProductListState) {
          const { page: savedPage, filter: savedFilter } = _subOrderProductListState;
          _subOrderProductListState = null;
          setPage(savedPage);
          setFilter(savedFilter);
          await fetchData(savedPage, savedFilter);
        } else {
          await fetchData(0);
        }
      } catch (err) {
        if (isMounted) console.error("Failed to load sub order products:", err);
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);


  //get order data
  const fetchData = async (pageNumber = 0, filterOverride = null) => {
    setOrderLoading(true);
    const activeFilter = filterOverride ?? filter;
    let result;
    try {
      result = await getSubOrderProductsByPaging(pageNumber, rowsPerPage, activeFilter);
    } catch (err) {
      setOrderLoading(false);
      return;
    }
    if (!result || typeof result !== "object" || !Array.isArray(result.content)) {
      setOrderLoading(false);
      return;
    }
    let productSet = result.content;

    //set total rows and pages
    setTotalPages(result.totalPages);
    setTotalRows(result?.totalElements || productSet?.length || 0);

    const newSet = [];
    if (productSet?.length !== 0) {
      for (let x = 0; x < productSet?.length; x++) {
        let inputCount = productSet[x].subOrderItems
          ? productSet[x].subOrderItems.filter(
              (item) => item.itemType === "INPUT"
            ).length
          : 0;

        newSet.push(
          createDataForProducts(
            productSet[x].subProductName,
            "output",
            productSet[x].status,
            inputCount,
            productSet[x].subProductId
          )
        );
      }
    }
    setOrderLoading(false);

    // set rows to table
    setRows(newSet);
  };

  //creating data for rows according to Id
  function createDataForProducts(
    productName,
    outputItem,
    status,
    inputCount,
    action,
    productId
  ) {
    return {
      productName,
      outputItem,
      status,
      inputCount,
      action,
      productId,
    };
  }

  //columns for table
  const columns_order = [
    { id: "productName", label: "Product Name", minWidth: 100 },
    { id: "outputItem", label: "Output Item", minWidth: 100 },
    { id: "status", label: "Status", minWidth: 100 },
    { id: "inputCount", label: "Input Material Count", minWidth: 100 },
    { id: "action", label: "Actions", minWidth: 100 },
  ];

  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(value - 1);
  };

  // filter values
  const [filter, setFilter] = useState({
    _productName: "",
    _inputImportIds: [],
  });
  const [productNameSearch, setProductNameSearch] = useState("");

  //reset filters
  const resetFilters = () => {
    setSelectedInputItems([]);
    setProductNameSearch("");
    const emptyFilter = {
      _productName: "",
      _inputImportIds: [],
    };
    setFilter(emptyFilter);
    setPage(0);
    fetchData(0, emptyFilter);
  };

  //to handle changing filters
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleAction = (event, id) => {
    switch (event) {
      case "view":
        _subOrderProductListState = { page, filter };
        history.push("/template/sub_order_product_view/" + id);
        break;
      case "edit":
        setEditProductId(id);
        setOpenEditForm(true);
        break;
      case "delete":
        setPendingDeleteId(id);
        setOpenDeleteDialog(true);
        break;
      default:
        return;
    }
  };

  // input Item popup handlers
  const handleOpenInputItemTable = async () => {
    setOpenInputItemTable(true);
  };

  const handleCloseInputItemTable = async () => {
    setOpenInputItemTable(false);
    setFilter({
      ...filter,
      _inputImportIds: selectedInputItems,
    });
  };

  const handleConfirmDelete = async () => {
    setOpenDeleteDialog(false);
    if (!pendingDeleteId) return;
    setOrderLoading(true);
    const result = await deleteSubOrderProductById(pendingDeleteId);
    setOrderLoading(false);
    setPendingDeleteId(null);
    if (result?.success) {
      fetchData(0);
    } else {
      setDeleteError(
        result?.message ||
          "Cannot be deleted. This product is linked to existing orders."
      );
    }
  };


  return (
    <StyledPaper elevation={0}>
      {openForm ? (
        <SubOrderProductCreate setOpenForm={setOpenForm} />
      ) : openEditForm ? (
        <SubOrderProductEdit
          productId={editProductId}
          setOpenForm={setOpenEditForm}
          onSaved={() => { setOpenEditForm(false); fetchData(0); }}
        />
      ) : (
        <>
          <HeaderCard>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <ReceiptIcon fontSize="large" />
              </Grid>
              <Grid item xs>
                <Typography variant="h5" fontWeight="bold">
                  Sub Order Product Management
                </Typography>
                <Typography variant="body2">
                  View and manage Sub Order Products and their items.
                </Typography>
              </Grid>
              <Grid item>
                <RoleBasedRender
                  allowedRoles={["SUB_ORDER_CREATE", "ROLE_ADMIN"]}
                >
                  <ActionButton
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenForm(true)}
                  >
                    Add Sub Product
                  </ActionButton>
                </RoleBasedRender>
              </Grid>
            </Grid>
          </HeaderCard>

          <Box
            mb={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2}>
              <Tooltip title="Toggle Filters">
                <ActionButton
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterListIcon />}
                  onClick={toggleFilters}
                >
                  Filters
                </ActionButton>
              </Tooltip>
              <Chip
                label={`${totalRows} Categories`}
                variant="outlined"
                color="primary"
              />
            </Stack>
          </Box>

          {showFilters && (
            <FilterContainer>
              <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                Advanced Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} md={4}>
                  <TextField
                    label="Product Name"
                    placeholder="Search by product name..."
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={productNameSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => {
                      setProductNameSearch(e.target.value);
                      handleFilterChange("_productName", e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <SelectButton
                    startIcon={<InventoryIcon />}
                    onClick={handleOpenInputItemTable}
                    endIcon={
                      selectedInputItems.length > 0 ? (
                        <Chip size="small" label={selectedInputItems.length} />
                      ) : null
                    }
                  >
                    {selectedInputItems.length > 0
                      ? "Input Items Selected"
                      : "Select Input Items"}
                  </SelectButton>
                </Grid>
              </Grid>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Reset Filters">
                    <ActionButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<RestartAltIcon />}
                      onClick={resetFilters}
                    >
                      Reset
                    </ActionButton>
                  </Tooltip>
                  <Tooltip title="Apply Filters">
                    <ActionButton
                      variant="contained"
                      color="primary"
                      onClick={() => { setPage(0); fetchData(0, filter); }}
                    >
                      Apply Filters
                    </ActionButton>
                  </Tooltip>
                </Stack>
              </Box>
            </FilterContainer>
          )}

          <Divider sx={{ my: 2 }} />

          {orderLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 5 }}>
              <Typography variant="body1" color="textSecondary">
                No Sub Order Products found.
              </Typography>
            </Box>
          ) : (
            <>
            {deleteError && (
              <Alert
                severity="error"
                onClose={() => setDeleteError("")}
                sx={{ mb: 2 }}
              >
                {deleteError}
              </Alert>
            )}
            <TableItem
              setRow={null}
              dropDown={false}
              columns={columns_order}
              rows={rows}
              handleAction={handleAction}
              showActions={{
                view: "View Product",
                edit: "Edit Product",
                delete: "Delete Product",
              }}
              actionRoles={{
                view: ["SUB_ORDER_VIEW", "ROLE_ADMIN"],
                edit: ["SUB_ORDER_EDIT", "ROLE_ADMIN"],
                delete: ["SUB_ORDER_DELETE", "ROLE_ADMIN"],
              }}
            />
            </>
          )}
          {totalPages > 1 && (
            <PaginationContainer>
              <Pagination
                count={totalPages}
                page={page + 1}
                variant="outlined"
                shape="rounded"
                color="primary"
                onChange={handleChange}
                size="medium"
              />
            </PaginationContainer>
          )}
        </>
      )}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this sub order product? This action
            cannot be undone. Deletion will be blocked if this product has
            existing orders.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {openInputItemTable && (
        <ItemSelectingPopup
          setOpenTable={setOpenInputItemTable}
          openTable={openInputItemTable}
          handleCloseTable={handleCloseInputItemTable}
          setSelectedItems={setSelectedInputItems}
          selectedItems={selectedInputItems}
          setRows={setItemRows}
          rows={itemRows}
        />
      )}
    </StyledPaper>
  );
}

export default SubOrderProduct;
